import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { PasswordInput } from '../components/auth/PasswordInput';
import { useSeo } from '../hooks/useSeo';

export function Login() {
  useSeo('Sign In — Media Magic');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const { login, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!username || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    try {
      await login(username, password);
      navigate('/gallery');
    } catch {
      // Error is set in context
    }
  }

  const displayError = localError || error;

  return (
    <div className="auth-screen">
      <div className="auth-layout">
        <aside className="auth-story">
          <span className="story-kicker">Delivering just the right image for you!</span>
          <h1>Images Optimized for Every Screen. In Real Time.</h1>
          <p>
            Media Magic detects exact client image dimensions and instantly delivers perfectly sized images through a scalable low-latency pipeline designed to integrate seamlessly with your existing stack.
          </p>
          <ul className="benefit-list" aria-label="Product benefits">
            <li>Easy onboarding</li>
            <li>Production-grade performance</li>
            <li>Built-in operational visibility</li>
          </ul>
        </aside>

        <section className="auth-card-wrap">
          <div className="auth-card">
            <h2 className="auth-title">Welcome back</h2>
            <p className="auth-subtitle">Sign in to continue improving image performance.</p>

            {displayError && <div className="auth-error">{displayError}</div>}

            <button
              type="button"
              className="btn btn-google auth-submit"
              onClick={() => signInWithRedirect({ provider: 'Google' })}
              disabled={loading}
            >
              <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </button>

            <div className="auth-divider">
              <span>or continue with email</span>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <label className="auth-label" htmlFor="username">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="auth-input"
                placeholder="Enter your username"
                disabled={loading}
              />

              <label className="auth-label" htmlFor="password">
                Password
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={setPassword}
                placeholder="Enter your password"
                disabled={loading}
              />

              <button type="submit" className="btn btn-solid auth-submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="auth-footer">
              Forgot your password?{' '}
              <Link to="/forgot-password" className="auth-link">
                Reset it
              </Link>
            </p>
            <p className="auth-footer">
              New here?{' '}
              <Link to="/signup" className="auth-link">
                Create your account
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Login;
